import { z } from 'zod';
import { IdSchema, InstantSchema } from './common.ts';

/**
 * A note the office keeps about a record the outside world can read
 * (P5-09e).
 *
 * `docs/features/08-purchase-orders-bills.md` §2 puts **Internal Notes** on
 * the purchase order under an "Internal Only" heading, and P5-09c removed
 * the field rather than hiding it. The reason is the one this product has
 * now hit four times: the sub a purchase order is for reads **that
 * document**, Firestore rules are all-or-nothing per document, and a field
 * a component leaves out is still a field in the bytes their browser
 * received. A comment saying "never shown to the sub" is true of the
 * component and false of the document, which is the most dangerous shape a
 * comment can take.
 *
 * So the note is a **sibling document** with its own rule, not a field.
 * The alternative considered in §6 was a `subPurchaseOrders` mirror the sub
 * reads instead of the record; the sibling is cheaper and was the one the
 * doc called probably right — there is one reader, one writer, and nothing
 * to derive. A mirror earns its keep when the outside reader needs a
 * *transformed* view (`ownerChangeOrders` drops cost and recomputes price);
 * here they need the record exactly as it is, minus one thing.
 *
 * **One document per record**, at a fixed id. A note is a single piece of
 * prose somebody edits, not a thread — comments already exist for the
 * thread, and they have an audience model this deliberately does not.
 */
export const INTERNAL_NOTE_ID = 'note';

export const InternalNoteSchema = z.object({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /**
   * The note itself. Empty is allowed and means somebody cleared it —
   * deleting the document would work too, but an empty note that still
   * carries `updatedBy` says *who* cleared it, and that is the question
   * asked of a note that used to say something.
   */
  body: z.string().max(20_000).default(''),
  updatedAt: InstantSchema,
  updatedBy: IdSchema,
});
export type InternalNote = z.infer<typeof InternalNoteSchema>;

/** Whether there is anything worth showing a badge for. */
export function hasInternalNote(note: InternalNote | null | undefined): boolean {
  return (note?.body.trim().length ?? 0) > 0;
}
